import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Result,
  SuffixNum,
  toBase, fromBase, altsFor, fmt, UNITS,
  SpecCard, SpecValue,
} from "./_shared";

/**
 *  PUMP POWER & HEAD REQUIRED
 *  --------------------------------------------------------------
 *  Hydraulic power:   P_h = ρ · g · Q · H / 1000             [kW]
 *  Shaft (pump):      P_s = P_h / η_pump                     [kW]
 *  Motor electrical:  P_e = P_s / η_motor                    [kW]
 *
 *    ρ = 1000 kg/m³ (water)
 *    g = 9.81 m/s²
 *    Q in m³/s, H in m
 *
 *  Suggested motor size = next standard kW rating up from P_e.
 *  --------------------------------------------------------------
 */
const STD_MOTOR_KW = [
  0.37, 0.55, 0.75, 1.1, 1.5, 2.2, 3.0, 4.0, 5.5, 7.5, 11, 15, 18.5, 22,
  30, 37, 45, 55, 75, 90, 110, 132, 160, 200, 250,
];

export default function PumpPowerCalculator() {
  const [flowVal, setFlowVal] = useState(5);
  const [flowUnit, setFlowUnit] = useState("Lps");
  const [headVal, setHeadVal] = useState(40);
  const [headUnit, setHeadUnit] = useState("m");
  const [etaPump, setEtaPump] = useState(65);          // %
  const [etaMotor, setEtaMotor] = useState(90);

  const Q_m3s = toBase("flow", flowVal, flowUnit);
  const H_m   = toBase("length_big", headVal, headUnit);
  const eta_p = (etaPump  || 0) / 100;
  const eta_m = (etaMotor || 0) / 100;

  const Ph = useMemo(() => (1000 * 9.81 * Q_m3s * H_m) / 1000, [Q_m3s, H_m]);     // hydraulic kW
  const Ps = eta_p > 0 ? Ph / eta_p : null;                                          // shaft kW
  const Pe = eta_m > 0 && Ps !== null ? Ps / eta_m : null;                           // electrical kW

  const motor_kW = useMemo(() => {
    if (Pe === null) return null;
    return STD_MOTOR_KW.find((k) => k >= Pe) ?? STD_MOTOR_KW[STD_MOTOR_KW.length - 1];
  }, [Pe]);

  // Efficiency sanity-check
  const effStatus = eta_p < 0.4 ? "warn"
                  : eta_p > 0.8 ? "warn"
                  : "ok";
  const effNote = eta_p < 0.4 ? "Pump efficiency below 40 % is unusually low — check the operating point."
                : eta_p > 0.8 ? "Pump efficiency above 80 % is optimistic — verify against the curve."
                : `${etaPump} % pump × ${etaMotor} % motor = ${fmt(eta_p * eta_m * 100, 0)} % wire-to-water.`;

  return (
    <ToolShell
      slug="pump-power"
      title="Pump power & head required"
      oneLiner="Hydraulic, shaft and motor power for a given duty point. Sanity-checks every centrifugal-pump quote."
      onExport={() => ({
        inputs: [
          { label: "Flow rate (duty point)", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
          { label: "Total dynamic head", value: headVal, unit: headUnit },
          { label: "Pump efficiency", value: etaPump, unit: "%" },
          { label: "Motor efficiency", value: etaMotor, unit: "%" },
        ],
        results: [
          { label: "Hydraulic power", value: fmt(Ph), unit: "kW",
            alts: Ph !== null ? [{ value: fmt(Ph * 1.34102), unit: "hp" }] : null },
          { label: "Shaft (pump) power", value: fmt(Ps), unit: "kW",
            alts: Ps !== null ? [{ value: fmt(Ps * 1.34102), unit: "hp" }] : null },
          { label: "Motor input power", value: fmt(Pe), unit: "kW",
            alts: Pe !== null ? [{ value: fmt(Pe * 1.34102), unit: "hp" }] : null },
          { label: "Suggested motor size", value: motor_kW ? fmt(motor_kW) : "—", unit: "kW (next standard)",
            alts: motor_kW ? [{ value: fmt(motor_kW * 1.34102), unit: "hp" }] : null },
          { label: "Wire-to-water efficiency", value: fmt(eta_p * eta_m * 100), unit: "%",
            status: effStatus, note: effNote },
        ],
        notes: [
          "Hydraulic power: P_h = ρ · g · Q · H / 1000.",
          "Shaft power:    P_s = P_h / η_pump.",
          "Motor input:    P_e = P_s / η_motor.",
        ],
        spec: motor_kW ? {
          title: "Pump power specification",
          body: `For a duty point of <strong>${flowVal} ${UNITS.flow.labels[flowUnit]}</strong> at <strong>${headVal} ${headUnit}</strong> TDH, hydraulic power is <strong>${fmt(Ph, 2)} kW</strong>. Assuming <strong>${etaPump} %</strong> pump and <strong>${etaMotor} %</strong> motor efficiency, the motor draws <strong>${fmt(Pe, 2)} kW</strong> electrical. Specify a <strong>${motor_kW} kW</strong> motor (next standard IEC rating). Wire-to-water efficiency = <strong>${fmt(eta_p * eta_m * 100, 0)} %</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Flow rate (duty point)">
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]} testid="pp-flow" />
        </Field>
        <Field label="Total dynamic head">
          <UnitNumber dim="length_big" value={headVal} onChange={setHeadVal} unit={headUnit} onUnitChange={setHeadUnit}
            units={["m", "ft"]} testid="pp-head" />
        </Field>
        <Field label="Pump efficiency" hint="65 % typical for centrifugal at BEP · 50–60 % for small or off-BEP.">
          <PercentInput value={etaPump} onChange={setEtaPump} testid="pp-etap" />
        </Field>
        <Field label="Motor efficiency" hint="90 % typical TEFC IE2 · 92–94 % for IE3.">
          <PercentInput value={etaMotor} onChange={setEtaMotor} testid="pp-etam" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Result label="Hydraulic power"
          primary={{ value: fmt(Ph), unit: "kW" }}
          alts={Ph !== null ? [{ value: fmt(Ph * 1.34102), unit: "hp" }] : null}
          testid="pp-result-hyd" />
        <Result label="Shaft (pump) power"
          primary={{ value: fmt(Ps), unit: "kW" }}
          alts={Ps !== null ? [{ value: fmt(Ps * 1.34102), unit: "hp" }] : null}
          testid="pp-result-shaft" />
        <Result label="Motor input power"
          primary={{ value: fmt(Pe), unit: "kW" }}
          alts={Pe !== null ? [{ value: fmt(Pe * 1.34102), unit: "hp" }] : null}
          testid="pp-result-motor" />
        <Result label="Suggested motor size"
          primary={{ value: motor_kW ? fmt(motor_kW) : "—", unit: "kW" }}
          alts={motor_kW ? [{ value: fmt(motor_kW * 1.34102), unit: "hp" }] : null}
          note={motor_kW ? "Next standard IEC rating up." : null}
          status={effStatus} testid="pp-result-size" />
      </div>

      {motor_kW && (
        <SpecCard
          eyebrow="Pump power specification"
          testid="pp-spec-card"
          footer={effNote}
        >
          For a duty point of <SpecValue>{flowVal} {UNITS.flow.labels[flowUnit]}</SpecValue> at{" "}
          <SpecValue>{headVal} {headUnit}</SpecValue> TDH, hydraulic power is{" "}
          <SpecValue>{fmt(Ph, 2)} kW</SpecValue>. Assuming{" "}
          <SpecValue>{etaPump} %</SpecValue> pump and{" "}
          <SpecValue>{etaMotor} %</SpecValue> motor efficiency, the motor draws{" "}
          <SpecValue>{fmt(Pe, 2)} kW</SpecValue> electrical. Specify a{" "}
          <SpecValue>{motor_kW} kW</SpecValue> motor (next standard IEC rating).
        </SpecCard>
      )}
    </ToolShell>
  );
}

function PercentInput({ value, onChange, testid }) {
  // Delegates to the shared SuffixNum so the input keeps a local buffer
  // and correctly accepts '0', '0.5', '0,5', '.5', empty-then-type flows.
  return <SuffixNum value={value} onChange={onChange} suffix="%" testid={testid} />;
}
